import React, { useState, useRef, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const PhotoCapture = ({ ticketId, ticketNumber, isOnline, onPhotoSaved, savePhotoLocally, getLocalPhotos, queueAction }) => {
  const [photos, setPhotos] = useState([]);
  const [localPhotos, setLocalPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [viewPhoto, setViewPhoto] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const loadPhotos = useCallback(async () => {
    // Load remote photos
    if (isOnline) {
      try {
        const { data } = await supabase?.from('ticket_photos')?.select('*')?.eq('ticket_id', ticketId)?.order('created_at', { ascending: false });
        setPhotos(data || []);
      } catch (e) {
        console.error('Error loading photos:', e);
      }
    }
    // Load local/offline photos
    if (getLocalPhotos) {
      const local = await getLocalPhotos(ticketId);
      setLocalPhotos(local || []);
    }
  }, [ticketId, isOnline, getLocalPhotos]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  const stopCamera = useCallback(() => {
    if (streamRef?.current) {
      streamRef?.current?.getTracks()?.forEach(t => t?.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
    setCameraError('');
  }, []);

  const startCamera = async () => {
    setCameraError('');
    setShowCamera(true);
    try {
      const stream = await navigator.mediaDevices?.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      if (videoRef?.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      setCameraError('Camera access denied or unavailable. Please use file upload instead.');
      setShowCamera(false);
    }
  };

  const capturePhoto = () => {
    if (!videoRef?.current || !canvasRef?.current) return;
    const video = videoRef?.current;
    const canvas = canvasRef?.current;
    canvas.width = video?.videoWidth;
    canvas.height = video?.videoHeight;
    canvas?.getContext('2d')?.drawImage(video, 0, 0);
    const base64 = canvas?.toDataURL('image/jpeg', 0.85);
    setPreview(base64);
    setPreviewFile(null);
    stopCamera();
  };

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (!file?.type?.startsWith('image/')) {
      setErrorMsg('Please select an image file.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPreview(ev?.target?.result);
      setPreviewFile(file);
    };
    reader?.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSavePhoto = async () => {
    if (!preview) return;
    setUploading(true);
    setErrorMsg('');
    setSuccessMsg('');
    const fileName = `tickets/${ticketId}/${Date.now()}_${ticketNumber?.replace(/[^a-z0-9]/gi, '_')}.jpg`;

    try {
      if (isOnline) {
        // Convert base64 to blob
        const base64 = preview;
        const byteString = atob(base64?.split(',')?.[1]);
        const mimeString = base64?.split(',')?.[0]?.split(':')?.[1]?.split(';')?.[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString?.length; i++) ia[i] = byteString?.charCodeAt(i);
        const blob = new Blob([ab], { type: mimeString });

        const { error: uploadError } = await supabase?.storage?.from('ticket-photos')?.upload(fileName, blob, { upsert: true });
        if (uploadError) throw uploadError;

        const { data: urlData } = supabase?.storage?.from('ticket-photos')?.getPublicUrl(fileName);
        const { error: dbError } = await supabase?.from('ticket_photos')?.insert({
          ticket_id: ticketId,
          file_name: fileName,
          file_url: urlData?.publicUrl,
          caption: caption?.trim() || ''
        });
        if (dbError) throw dbError;
        setSuccessMsg('Photo uploaded successfully!');
      } else {
        // Save locally and queue for sync
        if (savePhotoLocally) {
          await savePhotoLocally(ticketId, preview, caption?.trim() || '', fileName);
        }
        if (queueAction) {
          await queueAction('UPLOAD_PHOTO', {
            ticketId,
            base64: preview,
            caption: caption?.trim() || '',
            fileName
          });
        }
        setSuccessMsg('Photo saved offline. Will sync when online.');
      }
      setPreview(null);
      setPreviewFile(null);
      setCaption('');
      await loadPhotos();
      onPhotoSaved?.();
    } catch (err) {
      setErrorMsg('Failed to save photo. Please try again.');
      console.error('Photo save error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (photo) => {
    try {
      await supabase?.storage?.from('ticket-photos')?.remove([photo?.file_name]);
      await supabase?.from('ticket_photos')?.delete()?.eq('id', photo?.id);
      setPhotos(prev => prev?.filter(p => p?.id !== photo?.id));
      if (viewPhoto?.id === photo?.id) setViewPhoto(null);
    } catch (err) {
      setErrorMsg('Failed to delete photo.');
    }
  };

  const allPhotos = [
    ...photos?.map(p => ({ ...p, isLocal: false })),
    ...localPhotos?.filter(lp => !lp?.synced)?.map(lp => ({ id: lp?.localId, file_url: lp?.base64, caption: lp?.caption, isLocal: true, savedAt: lp?.savedAt }))
  ];

  return (
    <div className="p-5 border-b border-border">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Icon name="Camera" size={16} color="currentColor" />
          Job Photos
          {allPhotos?.length > 0 && (
            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">{allPhotos?.length}</span>
          )}
        </h3>
        {!isOnline && (
          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full flex items-center gap-1">
            <Icon name="WifiOff" size={10} color="currentColor" />
            Offline
          </span>
        )}
      </div>
      {/* Camera View */}
      {showCamera && (
        <div className="mb-4 rounded-xl overflow-hidden bg-black relative">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full max-h-64 object-cover"
          />
          <canvas ref={canvasRef} className="hidden" />
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-3">
            <button
              onClick={capturePhoto}
              className="w-14 h-14 bg-white rounded-full border-4 border-primary shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
            >
              <div className="w-10 h-10 bg-primary rounded-full" />
            </button>
            <button
              onClick={stopCamera}
              className="w-10 h-10 bg-black/60 rounded-full flex items-center justify-center"
            >
              <Icon name="X" size={18} color="white" />
            </button>
          </div>
        </div>
      )}
      {/* Preview */}
      {preview && !showCamera && (
        <div className="mb-4">
          <div className="relative rounded-xl overflow-hidden bg-black mb-3">
            <img src={preview} alt="Photo preview" className="w-full max-h-64 object-contain" />
            <button
              onClick={() => { setPreview(null); setPreviewFile(null); }}
              className="absolute top-2 right-2 w-8 h-8 bg-black/60 rounded-full flex items-center justify-center"
            >
              <Icon name="X" size={14} color="white" />
            </button>
          </div>
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e?.target?.value)}
            placeholder="Add a caption (optional)"
            className="w-full px-3 py-2 border border-border rounded-xl text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary mb-2"
          />
          <button
            onClick={handleSavePhoto}
            disabled={uploading}
            className="w-full py-2.5 px-4 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {uploading ? (
              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>
            ) : (
              <><Icon name="Upload" size={14} color="currentColor" /> {isOnline ? 'Upload Photo' : 'Save Offline'}</>
            )}
          </button>
        </div>
      )}
      {/* Camera Error */}
      {cameraError && (
        <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-xl mb-3">
          <Icon name="AlertTriangle" size={14} color="#d97706" />
          <p className="text-xs text-yellow-700">{cameraError}</p>
        </div>
      )}
      {/* Success/Error Messages */}
      {successMsg && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl mb-3">
          <Icon name="CheckCircle" size={14} color="#16a34a" />
          <p className="text-xs text-green-700">{successMsg}</p>
        </div>
      )}
      {errorMsg && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl mb-3">
          <Icon name="AlertCircle" size={14} color="#ef4444" />
          <p className="text-xs text-red-700">{errorMsg}</p>
        </div>
      )}
      {/* Action Buttons */}
      {!preview && !showCamera && (
        <div className="flex gap-2 mb-4">
          <button
            onClick={startCamera}
            className="flex-1 py-2.5 px-3 bg-primary/10 text-primary rounded-xl text-sm font-medium hover:bg-primary/20 transition-colors flex items-center justify-center gap-2"
          >
            <Icon name="Camera" size={16} color="currentColor" />
            Camera
          </button>
          <button
            onClick={() => fileInputRef?.current?.click()}
            className="flex-1 py-2.5 px-3 bg-muted text-foreground rounded-xl text-sm font-medium hover:bg-muted/80 transition-colors flex items-center justify-center gap-2"
          >
            <Icon name="ImagePlus" size={16} color="currentColor" />
            Upload
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      )}
      {/* Photo Grid */}
      {allPhotos?.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {allPhotos?.map((photo, idx) => (
            <button
              key={photo?.id || idx}
              onClick={() => setViewPhoto(photo)}
              className="relative aspect-square rounded-xl overflow-hidden bg-muted border border-border hover:opacity-90 transition-opacity"
            >
              <img
                src={photo?.file_url}
                alt={photo?.caption || `Job photo ${idx + 1}`}
                className="w-full h-full object-cover"
              />
              {photo?.isLocal && (
                <div className="absolute top-1 right-1 w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center">
                  <Icon name="Clock" size={10} color="white" />
                </div>
              )}
            </button>
          ))}
        </div>
      )}
      {allPhotos?.length === 0 && !preview && !showCamera && (
        <p className="text-xs text-muted-foreground text-center py-3">No photos yet. Capture job site photos above.</p>
      )}
      {/* Full-screen Photo Viewer */}
      {viewPhoto && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col">
          <div className="flex items-center justify-between p-4">
            <button onClick={() => setViewPhoto(null)} className="text-white flex items-center gap-2">
              <Icon name="ArrowLeft" size={20} color="white" />
              <span className="text-sm">Back</span>
            </button>
            {!viewPhoto?.isLocal && (
              <button
                onClick={() => handleDeletePhoto(viewPhoto)}
                className="w-9 h-9 bg-red-500/80 rounded-full flex items-center justify-center"
              >
                <Icon name="Trash2" size={16} color="white" />
              </button>
            )}
          </div>
          <div className="flex-1 flex items-center justify-center px-4">
            <img src={viewPhoto?.file_url} alt={viewPhoto?.caption || 'Job photo'} className="max-w-full max-h-full object-contain rounded-xl" />
          </div>
          {(viewPhoto?.caption || viewPhoto?.isLocal) && (
            <div className="p-4">
              {viewPhoto?.caption && <p className="text-white text-sm text-center">{viewPhoto?.caption}</p>}
              {viewPhoto?.isLocal && (
                <p className="text-yellow-400 text-xs text-center mt-1 flex items-center justify-center gap-1">
                  <Icon name="Clock" size={12} color="currentColor" />
                  Pending sync
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PhotoCapture;
